import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PageBanner } from '../components/PageBanner';
import { ArrowRight, ShieldCheck, Target, Users, Landmark } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
const stages = [{
  number: 1,
  label: 'STAGE 1',
  title: 'Discovery',
  description: 'Firetree spends time with the organisation and its leadership to understand the basics and establish values and mission alignment.',
  color: '#A69070',
  icon: 'https://cdn.magicpatterns.com/uploads/36y1gaAJK4XxVqN6KPkUXz/1.png'
}, {
  number: 2,
  label: 'STAGE 2',
  title: 'Relationship grant(s)',
  description: 'A single grant or series of grants to create the time and space for both the organisation and Firetree to understand each other.',
  color: '#C4342D',
  icon: 'https://cdn.magicpatterns.com/uploads/wAJWwCwML3Y2ghHmEu7pky/2.png'
}, {
  number: 3,
  label: 'STAGE 3',
  title: 'Long-term partnership',
  description: 'Through sustained funding and engagement, we provide long-term, unrestricted grants which enable the leader and organisation to focus on maximizing impact.',
  color: '#2D2A26',
  icon: 'https://cdn.magicpatterns.com/uploads/41jmwrK2GuaDdnUPmnu4S2/3.png'
}, {
  number: 4,
  label: 'STAGE 4',
  title: 'Fund portfolio',
  description: 'Organisations with demonstrated impact, strong leadership and established accountability mechanisms are considered for inclusion in the Firetree Fund portfolio.',
  color: '#243462',
  icon: 'https://cdn.magicpatterns.com/uploads/uh5eoGe3NZJ1prZUi38xps/4.png'
}];
function ScrollProgressSection({
  stages
}: {
  stages: typeof stages;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [activeStage, setActiveStage] = useState(0);
  const [stageProgress, setStageProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const wrapperHeight = wrapperRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      // Calculate how far through the wrapper we've scrolled
      // When top of wrapper hits top of viewport: scrollProgress = 0
      // When bottom of wrapper hits bottom of viewport: scrollProgress = 1
      const scrollStart = rect.top;
      const scrollEnd = rect.bottom - viewportHeight;
      const scrollRange = wrapperHeight - viewportHeight;
      let scrollProgress = 0;
      if (scrollStart <= 0 && scrollEnd >= 0) {
        scrollProgress = Math.abs(scrollStart) / scrollRange;
      } else if (scrollStart > 0) {
        scrollProgress = 0;
      } else {
        scrollProgress = 1;
      }
      // Clamp between 0 and 1
      scrollProgress = Math.max(0, Math.min(1, scrollProgress));
      // Map to stage (0-3) and progress within stage (0-1)
      const totalStages = stages.length;
      const stageIndex = Math.floor(scrollProgress * totalStages);
      const currentStage = Math.min(stageIndex, totalStages - 1);
      const progressInStage = scrollProgress * totalStages % 1;
      setActiveStage(currentStage);
      setStageProgress(progressInStage);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, [stages.length]);
  return <div ref={wrapperRef} style={{
    height: '400vh'
  }} className="relative" data-id="element-567">
      <div className="sticky top-0 min-h-[80vh] flex flex-col justify-center pt-16 pb-8 bg-brand-cream" data-id="element-568">
        <div className="w-full max-w-7xl px-6 sm:px-10 lg:px-16 mx-auto" data-id="element-569">
          {/* Progress Bar */}
          <div className="mb-12" data-id="element-570">
            <div className="flex items-center gap-1" data-id="element-571">
              {stages.map((stage, i) => {
              const isActive = i === activeStage;
              const isCompleted = i < activeStage;
              const fillPercentage = isActive ? stageProgress * 100 : isCompleted ? 100 : 0;
              return <div key={stage.number} className="flex-1" data-id="element-572">
                    <div className="text-center mb-3" data-id="element-573">
                      <span className="text-sm font-bold tracking-[0.15em] uppercase transition-colors duration-300" style={{
                    color: isActive || isCompleted ? stage.color : '#B8A896'
                  }} data-id="element-574">
                        {stage.label}
                      </span>
                    </div>
                    <div className="relative h-2 bg-brand-shape/20 rounded-full overflow-hidden" data-id="element-575">
                      <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-300 ease-out" style={{
                    width: `${fillPercentage}%`,
                    backgroundColor: stage.color
                  }} data-id="element-576" />
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* All Stage Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-577">
            {stages.map((stage, i) => {
            const isActive = i === activeStage;
            const isCompleted = i < activeStage;
            return <div key={stage.number} className="rounded-2xl p-6 lg:p-7 transition-all duration-500 ease-out border" style={{
              opacity: isActive ? 1 : isCompleted ? 0.85 : 0.35,
              transform: isActive ? 'scale(1.02)' : 'scale(1)',
              borderColor: isActive || isCompleted ? stage.color : 'rgba(166, 144, 112, 0.3)',
              borderTopWidth: isActive ? '3px' : '1px',
              borderTopColor: isActive || isCompleted ? stage.color : 'rgba(166, 144, 112, 0.3)',
              boxShadow: isActive ? '0 10px 30px -5px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.04)',
              backgroundColor: isActive ? '#F8F3EE' : 'rgba(248,243,238,0.5)'
            }} data-id="element-578">
                  <div className="mb-3 h-16 flex items-center" data-id="element-579">
                    <img src={stage.icon} alt={`Stage ${stage.number}`} className="h-14 w-auto object-contain transition-opacity duration-500" style={{
                  opacity: isActive ? 0.6 : isCompleted ? 0.4 : 0.2
                }} data-id="element-580" />
                  </div>
                  <h3 className="text-xl font-serif mb-3 transition-colors duration-500" style={{
                color: '#2D2A26'
              }} data-id="element-581">
                    {stage.title}
                  </h3>
                  <p className="text-base leading-relaxed transition-colors duration-500" style={{
                color: isActive || isCompleted ? '#6B5E4F' : '#B8A896'
              }} data-id="element-582">
                    {stage.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}
export function OurProcessPage() {
  return <div className="pb-24" data-id="element-583">
      <PageBanner title="Our process" subtitle="How organisations join the Firetree Fund portfolio" image="https://cdn.magicpatterns.com/uploads/3xLkYvXr5XqeBT956oRjkv/Teach_for_the_Philippines_-_Website_Banner_-_Fellowship_Program_(VVES).jpg" data-id="element-584" />

      {/* Scroll-Driven Progress Section */}
      <ScrollProgressSection stages={stages} data-id="element-585" />

      {/* Bottom Section - What we assess */}
      <section className="bg-brand-card/30 py-24 px-4 sm:px-6 lg:px-8" data-id="element-586">
        <div className="max-w-7xl mx-auto" data-id="element-587">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20" data-id="element-588">
            {/* Left column — Sticky title, description & CTAs */}
            <div className="lg:col-span-4" data-id="element-589">
              <div className="lg:sticky lg:top-28" data-id="element-590">
                <AnimatedSection data-id="element-591">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-brand-charcoal mb-6" data-id="element-592">
                    What we assess
                  </h2>
                  <p className="text-brand-brown leading-relaxed mb-4" data-id="element-593">
                    This rubric reflects the synthesis of our approach rather
                    than a generic checklist. It should be viewed as a framework
                    built from years of direct operational experience.
                  </p>
                  <p className="text-brand-brown leading-relaxed mb-10 text-sm opacity-80" data-id="element-594">
                    It is not a tool a non-practitioner can apply from the
                    outside, a pass/fail gate, or a substitute for the proximity
                    and trust that comes from years of real partnership.
                  </p>

                  <div className="flex flex-col gap-4" data-id="element-595">
                    <button className="inline-flex items-center text-brand-red font-semibold hover:text-brand-charcoal transition-colors group" data-id="element-596">
                      Download the rubric
                      <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" data-id="element-597" />
                    </button>
                    <Link to="/who-we-fund" className="inline-flex items-center text-brand-red font-semibold hover:text-brand-charcoal transition-colors group" data-id="element-598">
                      Our portfolio
                      <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" data-id="element-599" />
                    </Link>
                  </div>
                </AnimatedSection>
              </div>
            </div>

            {/* Right column — Stacking cards */}
            <div className="lg:col-span-8" data-id="element-600">
              <div className="flex flex-col gap-6" data-id="element-601">
                {/* Card 1: Non-negotiables */}
                <div className="lg:sticky lg:top-28" style={{
                zIndex: 10
              }} data-id="element-602">
                  <AnimatedSection data-id="element-603">
                    <div className="bg-brand-cream rounded-2xl border border-brand-shape/50 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-id="element-604">
                      <div className="px-8 lg:px-10 py-2.5 border-b border-brand-shape/30 border-l-[3px]" style={{
                      borderLeftColor: '#A69070',
                      backgroundColor: 'rgba(166, 144, 112, 0.08)'
                    }} data-id="element-605">
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-brand-gold" data-id="element-606">
                          Non-negotiables
                        </span>
                      </div>
                      <div className="p-8 lg:p-10" data-id="element-607">
                        <div className="w-12 h-12 rounded-full bg-brand-card flex items-center justify-center mb-6 icon-hover" data-id="element-608">
                          <ShieldCheck className="w-6 h-6 text-brand-gold" strokeWidth={1} data-id="element-609" />
                        </div>
                        <h3 className="text-2xl font-serif text-brand-charcoal mb-6" data-id="element-610">
                          Non-negotiables
                        </h3>
                        <ul className="flex flex-col" data-id="element-611">
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-612">
                            Fills a genuine gap not covered by government or
                            existing services
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-613">
                            Decision-makers have deep knowledge of the issue,
                            community and context
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-614">
                            Our existing contextual knowledge of the geography
                          </li>
                          <li className="py-3 text-base text-brand-brown leading-relaxed" data-id="element-615">
                            Appropriate legal registration and structures are in
                            place
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>

                {/* Card 2: Model and impact */}
                <div className="lg:sticky lg:top-[10rem]" style={{
                zIndex: 20
              }} data-id="element-616">
                  <AnimatedSection data-id="element-617">
                    <div className="bg-brand-cream rounded-2xl border border-brand-shape/50 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-id="element-618">
                      <div className="px-8 lg:px-10 py-2.5 border-b border-brand-shape/30 border-l-[3px]" style={{
                      borderLeftColor: '#C4342D',
                      backgroundColor: 'rgba(196, 52, 45, 0.05)'
                    }} data-id="element-619">
                        <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{
                        color: '#C4342D'
                      }} data-id="element-620">
                          Model and impact
                        </span>
                      </div>
                      <div className="p-8 lg:p-10" data-id="element-621">
                        <div className="w-12 h-12 rounded-full bg-brand-card flex items-center justify-center mb-6 icon-hover" data-id="element-622">
                          <Target className="w-6 h-6 text-brand-gold" strokeWidth={1} data-id="element-623" />
                        </div>
                        <h3 className="text-2xl font-serif text-brand-charcoal mb-6" data-id="element-624">
                          Model and impact
                        </h3>
                        <ul className="flex flex-col" data-id="element-625">
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-626">
                            Clear understanding of root causes and
                            problem-solution fit
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-627">
                            Quality and internal coherence of the programme
                            model and activities
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-628">
                            How effectiveness is measured and how the model
                            evolves as a result
                          </li>
                          <li className="py-3 text-base text-brand-brown leading-relaxed" data-id="element-629">
                            Demonstrable results against the organisation's core
                            objectives
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>

                {/* Card 3: People and culture */}
                <div className="lg:sticky lg:top-[13rem]" style={{
                zIndex: 30
              }} data-id="element-630">
                  <AnimatedSection data-id="element-631">
                    <div className="bg-brand-cream rounded-2xl border border-brand-shape/50 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-id="element-632">
                      <div className="px-8 lg:px-10 py-2.5 border-b border-brand-shape/30 border-l-[3px]" style={{
                      borderLeftColor: '#2D2A26',
                      backgroundColor: 'rgba(45, 42, 38, 0.05)'
                    }} data-id="element-633">
                        <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{
                        color: '#2D2A26'
                      }} data-id="element-634">
                          People and culture
                        </span>
                      </div>
                      <div className="p-8 lg:p-10" data-id="element-635">
                        <div className="w-12 h-12 rounded-full bg-brand-card flex items-center justify-center mb-6 icon-hover" data-id="element-636">
                          <Users className="w-6 h-6 text-brand-gold" strokeWidth={1} data-id="element-637" />
                        </div>
                        <h3 className="text-2xl font-serif text-brand-charcoal mb-6" data-id="element-638">
                          People and culture
                        </h3>
                        <ul className="flex flex-col" data-id="element-639">
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-640">
                            Client and community focus embedded in team DNA
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-641">
                            Capacity to learn, adapt and redefine approaches
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-642">
                            Role clarity, competencies and talent across the
                            organisation
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-643">
                            Leadership quality and management style
                          </li>
                          <li className="py-3 text-base text-brand-brown leading-relaxed" data-id="element-644">
                            Collaborative, open culture with appropriate checks
                            and balances
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>

                {/* Card 4: Governance and operations */}
                <div className="lg:sticky lg:top-[16rem]" style={{
                zIndex: 40
              }} data-id="element-645">
                  <AnimatedSection data-id="element-646">
                    <div className="bg-brand-cream rounded-2xl border border-brand-shape/50 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-id="element-647">
                      <div className="px-8 lg:px-10 py-2.5 border-b border-brand-shape/30 border-l-[3px]" style={{
                      borderLeftColor: '#243462',
                      backgroundColor: 'rgba(36, 52, 98, 0.05)'
                    }} data-id="element-648">
                        <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{
                        color: '#243462'
                      }} data-id="element-649">
                          Governance and operations
                        </span>
                      </div>
                      <div className="p-8 lg:p-10" data-id="element-650">
                        <div className="w-12 h-12 rounded-full bg-brand-card flex items-center justify-center mb-6 icon-hover" data-id="element-651">
                          <Landmark className="w-6 h-6 text-brand-gold" strokeWidth={1} data-id="element-652" />
                        </div>
                        <h3 className="text-2xl font-serif text-brand-charcoal mb-6" data-id="element-653">
                          Governance and operations
                        </h3>
                        <ul className="flex flex-col" data-id="element-654">
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-655">
                            Strong SOPs for programmatic work and child
                            protection protocols
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-656">
                            Robust financial management systems and policies
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-657">
                            Compliance across HR, safeguarding and child
                            protection
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-658">
                            Engaged, diverse board with relevant expertise
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-659">
                            Fundraising strategy aligned with programmatic
                            vision
                          </li>
                          <li className="py-3 text-base text-brand-brown leading-relaxed" data-id="element-660">
                            Composition of management team and complementarity
                            of expertise
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}